<template>
  <!-- template for the modal component -->
  <transition name="modal">
    <div class="modal-mask" @click.stop="$emit('close')">
      <div class="modal-wrapper" @click.stop>
        <div class="modal-container">
          <div class="modal-header">
            <slot name="header">
              Connection details
            </slot>
          </div>

          <div class="modal-body">
            <slot name="body">
              <a-divider>
                <strong
                  >publicDNS
                  <a-icon type="cloud" class="section-icon" />
                </strong>
              </a-divider>
              <div
                class="item"
                v-clipboard:copy="publicDNS"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <span>DNS name:</span>
                <div>
                  {{ publicDNS }}
                  <button class="condetails-button" type="button">
                    <font-awesome-icon icon="copy" class="icon" />
                  </button>
                </div>
              </div>

              <div
                class="item"
                v-clipboard:copy="publicIP"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <span>Public IP:</span>
                <div>
                  {{ publicIP }}
                  <button class="condetails-button" type="button">
                    <font-awesome-icon icon="copy" class="icon" />
                  </button>
                </div>
              </div>
              <p />

              <!-- ECC -->
              <a-divider>
                <strong
                  >EOSCONNECT Core
                  <a-icon type="appstore" class="section-icon" />
                </strong>
              </a-divider>
              <div class="item">
                Web API
                <div>
                  <button
                    class="condetails-button"
                    type="button"
                    @click="openNewTab(getECCWebApi())"
                  >
                    <font-awesome-icon icon="external-link-alt" class="icon" />
                  </button>
                  <button
                    class="condetails-button"
                    type="button"
                    v-clipboard:copy="getECCWebApi()"
                    v-clipboard:success="
                      data => onCopy({ ...data, vpnWarning: useVPNSetup })
                    "
                    v-clipboard:error="onError"
                  >
                    <font-awesome-icon icon="copy" class="icon" />
                  </button>
                </div>
              </div>
              <div
                class="item"
                v-clipboard:copy="getECCOpc()"
                v-clipboard:success="
                  data => onCopy({ ...data, vpnWarning: useVPNSetup })
                "
                v-clipboard:error="onError"
              >
                OPC/UA endpoint
                <button class="condetails-button" type="button">
                  <font-awesome-icon icon="copy" class="icon" />
                </button>
              </div>
              <p />

              <!-- VPN -->
              <section v-show="useVPNSetup">
                <a-divider>
                  <strong
                    >VPN
                    <a-icon type="api" class="section-icon" />
                  </strong>
                </a-divider>
                <div class="item">
                  Connect via Powershell:
                  <a-tooltip placement="rightTop">
                    <template slot="title">
                      {{ TOOLTIP.vpnPowershell }}
                    </template>
                    <button
                      class="condetails-button"
                      type="button"
                      v-clipboard:copy="createVPNScript()"
                      v-clipboard:success="
                        () => onCopyConfirm({ text: VPN_COPY_TEXT })
                      "
                      v-clipboard:error="onError"
                    >
                      <font-awesome-icon icon="terminal" class="icon" />
                    </button>
                  </a-tooltip>
                </div>
                <div
                  class="item"
                  v-clipboard:copy="privateIP"
                  v-clipboard:success="onCopy"
                  v-clipboard:error="onError"
                >
                  <span>Private IP:</span>
                  <div>
                    <strong>
                      {{ privateIP }}
                    </strong>
                    <button class="condetails-button" type="button">
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
                <div
                  class="item"
                  v-clipboard:copy="vpnUser"
                  v-clipboard:success="onCopy"
                  v-clipboard:error="onError"
                >
                  <span>User:</span>
                  <div>
                    <strong>
                      {{ vpnUser }}
                    </strong>
                    <button class="condetails-button" type="button">
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
                <div
                  class="item"
                  v-clipboard:copy="vpnPassword"
                  v-clipboard:success="onCopy"
                  v-clipboard:error="onError"
                >
                  <span>Password:</span>
                  <div>
                    <strong>
                      {{ vpnPassword }}
                    </strong>
                    <button class="condetails-button" type="button">
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
              </section>
              <p />

              <!-- Machine -->
              <section>
                <a-divider>
                  <strong
                    >Machine
                    <a-icon type="desktop" class="section-icon" />
                  </strong>
                </a-divider>
                <div class="item">
                  Update hosts via Powershell:
                  <a-tooltip placement="rightTop">
                    <template slot="title">
                      {{ TOOLTIP.vpnHosts }}
                    </template>
                    <button
                      class="condetails-button"
                      type="button"
                      v-clipboard:copy="createHostsScript()"
                      v-clipboard:success="
                        () => onCopyConfirm({ text: HOST_COPY_TEXT })
                      "
                      v-clipboard:error="onError"
                    >
                      <font-awesome-icon icon="terminal" class="icon" />
                    </button>
                  </a-tooltip>
                </div>
                <div class="item">
                  <span>Computer name:</span>
                  <div>
                    <strong
                      v-clipboard:copy="computerName"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      {{ computerName }}
                    </strong>
                    <button
                      class="condetails-button"
                      type="button"
                      v-clipboard:copy="computerName"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
                <div class="item">
                  <span>User:</span>
                  <div>
                    <strong
                      v-clipboard:copy="winAdminUser"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      {{ winAdminUser }}
                    </strong>
                    <button
                      class="condetails-button"
                      type="button"
                      v-clipboard:copy="winAdminUser"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
                <div class="item">
                  <span>Password:</span>
                  <div>
                    <strong
                      v-clipboard:copy="winAdminPassword"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      {{ winAdminPassword }}
                    </strong>
                    <button
                      class="condetails-button"
                      type="button"
                      v-clipboard:copy="winAdminPassword"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <font-awesome-icon icon="copy" class="icon" />
                    </button>
                  </div>
                </div>
              </section>
            </slot>
          </div>

          <div class="modal-footer ">
            <slot name="footer">
              <button class="modal-default-button" @click="$emit('close')">
                Close
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "popup",
  props: {
    publicDNS: String,
    publicIP: String,
    privateIP: String,
    vpnUser: String,
    vpnPassword: String,
    winAdminUser: String,
    computerName: String,
    winAdminPassword: String,
    useVPNSetup: Boolean
  },
  data: function() {
    return {
      VPN_NAME: "EDN-VPN",
      HOST_COPY_TEXT: `A Powershell script was copied to the clipboard. This script adds/updates an entry in the hosts file of Windows. The script has to be copied into a Powershell console with administrator rights.`,
      VPN_COPY_TEXT: `A Powershell script was copied to the clipboard. This script adds/updates a VPN connection with name "EDN-VPN" and automatically connects to this VPN (split tunneling active). The script is to be copied into a Powershell Console.`,
      TOOLTIP: {
        vpnPowershell: `Copies a script to create a VPN connection automatically.`,
        vpnHosts: `Copies a script to update the %windir%\\system32\\drivers\\etc\\hosts file for reverse lookup.`
      }
    };
  },
  computed: {
    showVPNInfo: function() {
      return "  VPN. User: " + this.vpnUser + ", password: " + this.vpnPassword;
    },
    showWinAdminInfo: function() {
      return (
        "  Windows. User: " +
        this.winAdminUser +
        ", password: " +
        this.winAdminPassword
      );
    }
  },
  methods: {
    getECCWebApi() {
      if (this.useVPNSetup) {
        return "https://" + this.privateIP;
      }
      return "https://" + this.publicDNS;
    },
    getECCOpc() {
      if (this.useVPNSetup) {
        return "opc.tcp://" + this.privateIP + ":4843";
      }
      return "opc.tcp://" + this.publicDNS + ":4843";
    },
    openNewTab(link) {
      window.open(link, "_blank"); //, 'rel="noreferrer noopener"');
    },
    onCopy(value) {
      console.log(value);
      let text = value?.text;

      this.$notification.info({
        message: "Copied to clipboard",
        description: text
      });

      if (value.vpnWarning) {
        this.$notification.warn({
          message: "Active VPN connection necessary to use copied string!"
        });
      }
    },
    onCopyConfirm(value) {
      let text = value?.text;
      const notificationKey = `open${Date.now()}`;
      this.$notification.open({
        message: "Copied to clipboard",
        description: text,
        btn: h => {
          return h(
            "a-button",
            {
              props: {
                type: "primary",
                size: "small"
              },
              on: {
                click: () => this.$notification.close(notificationKey)
              }
            },
            "Confirm"
          );
        },
        key: notificationKey,
        duration: 10,
        onClose: close
      });
    },
    onError() {
      this.$notification.warn({
        message: "Failed to copy text!"
      });
    },
    createVPNScript() {
      // returns a powershell script to set/ update and connect the windows internal vpn with the VM vpn according to VPN-Settings.
      let escapedPsw = this.vpnPassword
        .replaceAll("`", "``")
        .replaceAll("$", "`$")
        .replaceAll('"', '`"');
      return (
        `$edn_availVPNs=Get-VPNConnection;if($edn_availVPNs -and $edn_availVPNs.Name.contains("${this.VPN_NAME}")){rasdial "${this.VPN_NAME}" /disconnect; Remove-VpnConnection -Name "${this.VPN_NAME}" -Force};` +
        `Add-VpnConnection -Name "${this.VPN_NAME}" -ServerAddress "${this.publicDNS}" -TunnelType "Sstp" -SplitTunneling -AuthenticationMethod "MsChapv2"` +
        `-EncryptionLevel "Required" -RememberCredential -IdleDisconnectSeconds 1800; rasdial "${this.VPN_NAME}" "${this.vpnUser}" "${escapedPsw}";Clear-Variable edn_availVPNs`
      );
    },
    createHostsScript() {
      // returns a powershell script to set/ update an entry in the %windir%\system32\drivers\etc\hosts file with format
      // publicIP EDN-VM
      return (
        `$hostspath = Join-Path -Path $env:windir -childpath "\\system32\\drivers\\etc\\hosts";$text = "${
          this.useVPNSetup ? this.privateIP : this.publicIP
        } ${this.computerName}";` +
        `if(Select-String -Path $hostspath -Pattern "^\\s*[^#].*${this.computerName}"){$regex = '^\\s*(\\d+\\.\\d+\\.\\d+\\.\\d+)\\s+${this.computerName}\\s*';` +
        `$newHosts = (Get-Content $hostspath) -replace $regex, $text } else ` +
        `{ $newHosts = Get-Content $hostspath; $newHosts += "\`r\`n"+$text };` +
        `Set-Content -Value $newHosts -Path $hostspath`
      );
    }
  }
};
</script>

// Scoped doesn't work with this slot for some reasone?
<style lang="scss">
@import "../styles/_variables.scss";

.condetails-button {
  background: none;
  border: none;
  cursor: pointer;
}
.section-icon {
  padding-left: 2px;
}
.item {
  display: flex;
  justify-content: space-between;
  & > span {
    width: 40%;
  }

  & > div {
    display: flex;
    justify-content: flex-end;
    text-align: right;
  }
}

.user-item {
  display: flex;
  justify-content: space-between;
  width: 80%;
}

.centered-label {
  text-align: center;
  padding-bottom: 8px;
}
.modal-mask {
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
  align-self: center;
}

.modal-container {
  width: 450px;
  border-radius: 0.1875rem;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

.modal-header h3 {
  margin-top: 0;
  text-align: center;
  color: $accentColor;
  font-family: "Rotis Sans Serif W01", Calibri, Arial;
  font-size: 2.375rem;
}

.modal-body {
  margin: 5px;
  text-align: left;
  font-family: Open Sans, sans-serif;
  strong {
    font-family: "RotisSansSerifW01-Bold", Calibri, Arial;
    font-size: 1.125rem;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 5px;
}

.modal-default-button {
  width: auto;
  font-size: 1rem;
  font-weight: 500;
  font-family: "Open Sans", sans-serif;
  border-color: $secondaryColor;
  background-color: $secondaryColor;
  height: 2.3125rem;
  padding: 0 0.625rem;
  border-radius: 0.1875rem;
  box-shadow: 0 0.5625rem 2.4375rem -0.6875rem rgba(0, 0, 0, 0.75);
  min-width: 0;
  color: white;
}

/*
    * The following styles are auto-applied to elements with
    * transition="modal" when their visibility is toggled
    * by Vue.js.
    *
    * You can easily play with the modal transition by editing
    * these styles.
    */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1);
  transform: scale(1);
}
</style>
